import {
  AngleUnit,
  AreaUnit,
  formatMass,
  LengthUnit,
  MassUnit,
  VolumeUnit,
} from '@fulcrum/lib';
import { FormattingSettings } from '@trimble/types';
import {
  AngleFormatter,
  AreaFormatter,
  LengthFormatter,
  VolumeFormatter,
} from '@trimble/utilities';
import { UnreachableCaseError } from 'ts-essentials';
import type { ObjectPropertiesSourceUnits } from './object-properties.types';

/** Fallback display unit when `settings.massUnit` is unset. */
const DEFAULT_MASS_DISPLAY_UNIT: MassUnit = 'kg';
const DEFAULT_LENGTH_SOURCE_UNIT: LengthUnit = 'mm';
const DEFAULT_AREA_SOURCE_UNIT: AreaUnit = 'm2';
const DEFAULT_VOLUME_SOURCE_UNIT: VolumeUnit = 'm3';
const DEFAULT_ANGLE_SOURCE_UNIT: AngleUnit = 'deg';

export type MeasureDisplayKind =
  | 'length'
  | 'area'
  | 'volume'
  | 'angle'
  | 'mass';

export interface MeasureDisplayUnits {
  length?: LengthUnit;
  area?: AreaUnit;
  volume?: VolumeUnit;
  angle?: AngleUnit;
}

/**
 * @intent Shared Length/Area/Volume/Angle/Mass display formatting for model
 * and custom property values.
 */
export function formatMeasureDisplay(
  kind: MeasureDisplayKind,
  value: number,
  settings: FormattingSettings,
  currentUnits: MeasureDisplayUnits
): string {
  switch (kind) {
    case 'length':
      return LengthFormatter.toDisplay({
        measuredValue: value,
        currentUnits: currentUnits.length ?? DEFAULT_LENGTH_SOURCE_UNIT,
        settings,
      });
    case 'area':
      return AreaFormatter.toDisplay({
        measuredValue: value,
        currentUnits: currentUnits.area ?? DEFAULT_AREA_SOURCE_UNIT,
        settings,
      });
    case 'volume':
      return VolumeFormatter.toDisplay({
        measuredValue: value,
        currentUnits: currentUnits.volume ?? DEFAULT_VOLUME_SOURCE_UNIT,
        settings,
      });
    case 'angle':
      return AngleFormatter.toDisplay({
        measuredValue: value,
        currentUnits: currentUnits.angle ?? DEFAULT_ANGLE_SOURCE_UNIT,
        settings,
      });
    case 'mass':
      // formatMass expects kilograms as its first arg (schema/CDM base unit) and
      // converts to the display unit passed as the second arg — same contract as
      // formatLength(mm, displayUnit) / formatArea(m², displayUnit).
      return formatMass(
        value,
        (settings.massUnit ?? DEFAULT_MASS_DISPLAY_UNIT) as MassUnit,
        settings.massDecimals
      );
    default:
      throw new UnreachableCaseError(kind);
  }
}

export function resolveMeasureSourceUnits(
  sourceUnits?: ObjectPropertiesSourceUnits
): MeasureDisplayUnits {
  return {
    length: (sourceUnits?.length ?? DEFAULT_LENGTH_SOURCE_UNIT) as LengthUnit,
    area: (sourceUnits?.area ?? DEFAULT_AREA_SOURCE_UNIT) as AreaUnit,
    volume: (sourceUnits?.volume ?? DEFAULT_VOLUME_SOURCE_UNIT) as VolumeUnit,
    angle: (sourceUnits?.angle ?? DEFAULT_ANGLE_SOURCE_UNIT) as AngleUnit,
  };
}
